import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { refreshTokenWithKeycloak } from '../helpers/AuthHelper';
import { AuthUser } from '../types/auth';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  idToken: string | null;
}

interface AuthContextType extends AuthState {
  saveAuth: (authData: Partial<AuthState>) => void;
  reAuthenticate: () => Promise<void>;
  logout: () => any;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const KeycloakAuthProvider = ({ children }: { children: ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    idToken: null,
  });

  const [refreshToken, setRefreshToken] = useState<string | null>(null);

  // Refresh token
  const reAuthenticate = async () => {
    if (!refreshToken) throw new Error('No refresh token available');
    const baseUrl = import.meta.env.VITE__KEYCLOAK_LOGIN_BASE_URL;
    const clientId = import.meta.env.VITE__KEYCLOAK_CLIENT_ID;
    try {
      const response = await refreshTokenWithKeycloak(
        refreshToken,
        clientId,
        `${baseUrl}/token`,
      );
      setAuthState((prev) => ({
        ...prev,
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      }));

      localStorage.setItem(
        'refreshToken',
        JSON.stringify({
          refreshToken: response.refresh_token,
        }),
      );
    } catch (error) {
      console.error('Re authentication failed:', error);
      logout();
    }
  };

  const saveAuth = (authData: Partial<AuthState>) => {
    const updatedState = {
      ...authState,
      ...authData,
      isAuthenticated: true,
    };
    setAuthState(updatedState);
    localStorage.setItem('authData', JSON.stringify(updatedState));
  };

  const logout = () => {
    // Replace this with Keycloak's logout endpoint logic
    console.log('logging out');
    const baseUrl = import.meta.env.VITE__KEYCLOAK_LOGIN_BASE_URL;

    const redirectUri = import.meta.env.VITE__APP_PORTAL_URL;

    const idToken = authState?.idToken ?? '';

    const logoutUrl = `${baseUrl}logout?post_logout_redirect_uri=${encodeURIComponent(
      redirectUri,
    )}&id_token_hint=${encodeURIComponent(idToken)}`;

    localStorage.removeItem('authData');

    window.location.replace(logoutUrl);
  };

  useEffect(() => {
    const storedAuth = localStorage.getItem('authData');
    if (storedAuth) {
      setAuthState((prev) => ({ ...prev, ...JSON.parse(storedAuth) }));
      setRefreshToken(JSON.parse(storedAuth).refreshToken);
    } else {
      setAuthState((prev) => ({ ...prev }));
    }
  }, []);

  return (
    <AuthContext.Provider
      value={useMemo(
        () => ({ ...authState, saveAuth, logout, reAuthenticate }),
        [authState],
      )}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      'useAuthContext must be used within a KeycloakAuthProvider',
    );
  }
  return context;
};
